import React, { useState, useEffect, useRef } from 'react';
import { Card, CircularProgress, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogContent, DialogTitle, IconButton, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Grid } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { apiCall } from '../main/ApiCall';
import '../css/main/Home.css';
import "../css/others/Table.css";
import { buildUserPayload } from '../main/buildUserPayload';
import CloseIcon from '@mui/icons-material/Close';
import { AuditScore, ProjectRadarData, ControlScore } from "../interFaces/interfaces"
const PMDashboard: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const clientId = localStorage.getItem("clientId");
  const [loading, setLoading] = useState({ auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true, controlsCount: true, normalAuditScores: true, integratedAuditScores: true });
  const [error, setError] = useState({ auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false, controlsCount: false, normalAuditScores: false, integratedAuditScores: false });
  const [selectedProject, setSelectedProject] = useState<{ projectId: string, auditId: string, projectName: string, campaignId: string, campaignName: string, checklistType: string } | null>(null);
  const [radarDialogOpen, setRadarDialogOpen] = useState(false);
  const [findingsDialogOpen, setFindingsDialogOpen] = useState(false);
  const [selectedProjectRadarData, setSelectedProjectRadarData] = useState<ProjectRadarData[]>([]);
  const [findingsData, setFindingsData] = useState<any>(null);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [integratedFindingsData, setIntegratedFindingsData] = useState<any>(null);
  const [integratedFindingsLoading, setIntegratedFindingsLoading] = useState(false);
  const [normalAuditScores, setNormalAuditScores] = useState<AuditScore[]>([]);
  const [integratedAuditScores, setIntegratedAuditScores] = useState<AuditScore[]>([]);
  const [normalCurrentPage, setNormalCurrentPage] = useState<number>(1);
  const [normalItemsPerPage, setNormalItemsPerPage] = useState<number>(5);
  const [normalTotalPages, setNormalTotalPages] = useState<number>(0);
  const [normalTotalElements, setNormalTotalElements] = useState<number>(0);
  const [integratedCurrentPage, setIntegratedCurrentPage] = useState<number>(1);
  const [integratedItemsPerPage, setIntegratedItemsPerPage] = useState<number>(5);
  const [integratedTotalPages, setIntegratedTotalPages] = useState<number>(0);
  const [integratedTotalElements, setIntegratedTotalElements] = useState<number>(0);
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState<string>("all");
  const [allCampaignNames, setAllCampaignNames] = useState<string[]>([]);
  const isMounted = useRef(true);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const COLORS = ['#FF6384', '#FF9F40', '#FFCD56', '#4BC0C0', '#36A2EB', '#9966FF', '#6d1629', '#C9CBCF'];
  const findingOrder = ["BEST_PRACTICE", "LARGELY_EFFECTIVE", "NEEDS_IMPROVMENT", "PARTIALLY_IMPLEMENTED", "INITIAL_ADHOC", "NOT_IMPLEMENTED"];
  const LoadingSpinner = ({ height = 200 }: { height?: number }) => (
    <Box display="flex" justifyContent="center" alignItems="center" height={height}>
      <CircularProgress />
    </Box>
  );
  const NoDataMessage = ({ height = 200 }: { height?: number }) => (
    <Box display="flex" justifyContent="center" alignItems="center" height={height}>
      <div className="no-data">No data available</div>
    </Box>
  );
  const getRatingClassName = (rating: string) => {
    if (!rating) return "rating-default";
    switch (rating.toLowerCase()) {
      case "high risk": return "rating-high-risk";
      case "needs improvement": return "rating-needs-improvement";
      case "satisfactory": return "rating-satisfactory";
      case "effective": return "rating-effective";
      case "optimized": return "rating-optimized";
      default: return "rating-default";
    }
  };
  const getFindingClassName = (findingType: string, value: number) => {
    if (value === 0) return "rating-zero";
    switch (findingType) {
      case "BEST_PRACTICE": return "rating-optimized";
      case "LARGELY_EFFECTIVE": return "rating-effective";
      case "NEEDS_IMPROVMENT": return "rating-needs-improvement";
      case "PARTIALLY_IMPLEMENTED": return "rating-partially-implemented";
      case "INITIAL_ADHOC": return "rating-initial-adhoc";
      case "NOT_IMPLEMENTED": return "rating-high-risk";
      default: return "rating-default";
    }
  };
  useEffect(() => {
    const fetchNormalAuditScores = async () => {
      try {
        setLoading(prev => ({ ...prev, normalAuditScores: true }));
        setError(prev => ({ ...prev, normalAuditScores: false }));
        const campaignParam = selectedCampaignFilter !== "all" ? `&campaignName=${encodeURIComponent(selectedCampaignFilter)}` : "";
        const normalData = await apiCall("get", `/api/dashboard/audit-scores?clientId=${clientId}&checklistType=NORMAL&page=${normalCurrentPage - 1}&size=${normalItemsPerPage}${campaignParam}`);
        if (isMounted.current && normalData) {
          setNormalTotalPages(normalData.totalPages);
          setNormalTotalElements(normalData.totalElements);
          setNormalAuditScores(normalData.content || []);
          if (normalData.content?.length) {
            setAllCampaignNames(prev => {
              const combined = new Set([...prev, ...normalData.content.map((a: AuditScore) => a.campaignName).filter(Boolean)]);
              return Array.from(combined).sort() as string[];
            });
          }
          const timeoutId = setTimeout(() => {
            if (isMounted.current) setLoading(prev => ({ ...prev, normalAuditScores: false }));
          }, 300);
          timeoutRefs.current.push(timeoutId);
        }
      } catch (err) {
        if (isMounted.current) {
          setError(prev => ({ ...prev, normalAuditScores: true }));
          setLoading(prev => ({ ...prev, normalAuditScores: false }));
        }
      }
    };
    fetchNormalAuditScores();
  }, [clientId, normalCurrentPage, normalItemsPerPage, selectedCampaignFilter]);
  useEffect(() => {
    const fetchIntegratedAuditScores = async () => {
      try {
        setLoading(prev => ({ ...prev, integratedAuditScores: true }));
        setError(prev => ({ ...prev, integratedAuditScores: false }));
        const campaignParam = selectedCampaignFilter !== "all" ? `&campaignName=${encodeURIComponent(selectedCampaignFilter)}` : "";
        const integratedData = await apiCall("get", `/api/dashboard/audit-scores?clientId=${clientId}&checklistType=INTEGRATED&page=${integratedCurrentPage - 1}&size=${integratedItemsPerPage}${campaignParam}`);
        if (isMounted.current && integratedData) {
          setIntegratedTotalPages(integratedData.totalPages);
          setIntegratedTotalElements(integratedData.totalElements);
          setIntegratedAuditScores(integratedData.content || []);
          if (integratedData.content?.length) {
            setAllCampaignNames(prev => {
              const combined = new Set([...prev, ...integratedData.content.map((a: AuditScore) => a.campaignName).filter(Boolean)]);
              return Array.from(combined).sort() as string[];
            });
          }
          const timeoutId = setTimeout(() => {
            if (isMounted.current) setLoading(prev => ({ ...prev, integratedAuditScores: false }));
          }, 300);
          timeoutRefs.current.push(timeoutId);
        }
      } catch (err) {
        if (isMounted.current) {
          setError(prev => ({ ...prev, integratedAuditScores: true }));
          setLoading(prev => ({ ...prev, integratedAuditScores: false }));
        }
      }
    };

    fetchIntegratedAuditScores();
  }, [clientId, integratedCurrentPage, integratedItemsPerPage, selectedCampaignFilter]);
  useEffect(() => {
    isMounted.current = true;
    const fetchData = async () => {
      let requestBody: { field: string; operator: string; value: string }[] = [{ field: "clientId", operator: "=", value: clientId ?? "" }];
      const payload = buildUserPayload();
      if (payload) {
        requestBody.push(payload);
      }
      try {
        setLoading(prev => ({ ...prev, auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true, controlsCount: true }));
        setError(prev => ({ ...prev, auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false, controlsCount: false }));
        const response = await apiCall("post", `/api/dashboard/summary?clientId=${clientId}`, requestBody);
        if (!response) throw new Error("No data received from API");
        setData(response);
      } catch (err) {
        setError(prev => ({ ...prev, auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true, controlsCount: true }));
      } finally {
        setLoading(prev => ({ ...prev, auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false, controlsCount: false }));
      }
    };
    fetchData();
    return () => {
      isMounted.current = false;
      timeoutRefs.current.forEach(clearTimeout);
      timeoutRefs.current = [];
    };
  }, [clientId]);
  const handleCampaignFilterChange = (event: SelectChangeEvent) => {
    setSelectedCampaignFilter(event.target.value);
    setNormalCurrentPage(1);
    setIntegratedCurrentPage(1);
  };
  const fetchFindingsData = async (campaignId: string, projectId: string) => {
    try {
      setFindingsLoading(true);
      const response = await apiCall("get", `/api/dashboard/audit-scores/risk?campaignId=${campaignId}&projectId=${projectId}`);
      if (isMounted.current) {
        setFindingsData(response);
        setFindingsDialogOpen(true);
      }
    } catch (error) {
      console.error("Error fetching findings data:", error);
    } finally {
      setFindingsLoading(false);
    }
  };
  const fetchIntegratedFindingsData = async (campaignId: string, projectId: string) => {
    try {
      setIntegratedFindingsLoading(true);
      const response = await apiCall("get", `/api/dashboard/audit-scores/risk?campaignId=${campaignId}&projectId=${projectId}`);
      if (isMounted.current) {
        setIntegratedFindingsData(response);
      }
    } catch (error) {
      console.error("Error fetching integrated findings data:", error);
    } finally {
      setIntegratedFindingsLoading(false);
    }
  };
  const handleProjectClick = (projectId: string, auditId: string, projectName: string, checklistType: string, campaignId: string, campaignName: string) => {
    setSelectedProject({ projectId, auditId, projectName, campaignId, campaignName, checklistType });
    if (checklistType === "NORMAL") {
      fetchFindingsData(campaignId, projectId);
    } else {
      const projectAudit = integratedAuditScores.find((audit: AuditScore) => audit.auditId === auditId && audit.projectId === projectId);
      if (projectAudit) {
        const auditScoreData = projectAudit.auditScoreData as any;
        const controlScores: ControlScore[] = auditScoreData?.controlScores || [];
        const radarData = controlScores.map((control: ControlScore) => ({ subject: control.controlType, score: control.score, weightedScore: control.weightedScore, fullMark: 5 }));
        setSelectedProjectRadarData(radarData);
        fetchIntegratedFindingsData(campaignId, projectId);
        setRadarDialogOpen(true);
      }
    }
  };
  const handleCloseRadarDialog = () => {
    setRadarDialogOpen(false);
    setSelectedProject(null);
    setSelectedProjectRadarData([]);
    setIntegratedFindingsData(null);
  };
  const handleCloseFindingsDialog = () => {
    setFindingsDialogOpen(false);
    setSelectedProject(null);
    setFindingsData(null);
  };
  const getControlScoreByType = (auditScore: AuditScore, controlType: string) => {
    const auditScoreData = auditScore.auditScoreData as any;
    const controlScores: ControlScore[] = auditScoreData?.controlScores || [];
    return controlScores.find((control: ControlScore) => control.controlType === controlType);
  };
  const getAllControlTypes = (auditScores: AuditScore[]) => {
    if (!auditScores) return [];
    const allControlTypes = new Set<string>();
    auditScores.forEach((auditScore: AuditScore) => {
      const auditScoreData = auditScore.auditScoreData as any;
      const controlScores: ControlScore[] = auditScoreData?.controlScores || [];
      controlScores.forEach((control: ControlScore) => allControlTypes.add(control.controlType));
    });
    return Array.from(allControlTypes);
  };
  const formatWeightedScore = (weightedScore: number): string => `${weightedScore}%`;
  const renderNormalPagination = () => {
    const startItem = (normalCurrentPage - 1) * normalItemsPerPage + 1;
    const endItem = Math.min(normalCurrentPage * normalItemsPerPage, normalTotalElements);
    const pageNumbers = [];
    const maxPageNumbers = 5;
    let startPage = Math.max(1, normalCurrentPage - Math.floor(maxPageNumbers / 2));
    let endPage = Math.min(normalTotalPages, startPage + maxPageNumbers - 1);
    if (endPage - startPage + 1 < maxPageNumbers) startPage = Math.max(1, endPage - maxPageNumbers + 1);
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    return (
      <div className="pagination">
        <button className="info">Showing {startItem} to {endItem} of {normalTotalElements} items</button>
        <div className="pagination-controls">
          <button onClick={() => setNormalCurrentPage(1)} className="pagination-btn" disabled={normalCurrentPage === 1}><span className="small-pipe">|</span>&#8249;</button>
          <button onClick={() => setNormalCurrentPage(Math.max(1, normalCurrentPage - 1))} className="pagination-btn" disabled={normalCurrentPage === 1}>&#8249;</button>
          {pageNumbers.map((page) => (
            <button key={`normal-page-${page}`} onClick={() => setNormalCurrentPage(page)} className={`pagination-btn ${normalCurrentPage === page ? "active" : ""}`}>{page}</button>
          ))}
          <button onClick={() => setNormalCurrentPage(Math.min(normalTotalPages, normalCurrentPage + 1))} className="pagination-btn" disabled={normalCurrentPage === normalTotalPages}>&#8250;</button>
          <button onClick={() => setNormalCurrentPage(normalTotalPages)} className="pagination-btn" disabled={normalCurrentPage === normalTotalPages}>&#8250;<span className="small-pipe">|</span></button>
        </div>
        <div>
          <select value={normalItemsPerPage} onChange={(e) => { setNormalItemsPerPage(Number(e.target.value)); setNormalCurrentPage(1); }} className="items-perpage-select">
            <option value={5}>5</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
    );
  };
  const renderIntegratedPagination = () => {
    const startItem = (integratedCurrentPage - 1) * integratedItemsPerPage + 1;
    const endItem = Math.min(integratedCurrentPage * integratedItemsPerPage, integratedTotalElements);
    const pageNumbers = [];
    const maxPageNumbers = 5;
    let startPage = Math.max(1, integratedCurrentPage - Math.floor(maxPageNumbers / 2));
    let endPage = Math.min(integratedTotalPages, startPage + maxPageNumbers - 1);
    if (endPage - startPage + 1 < maxPageNumbers) startPage = Math.max(1, endPage - maxPageNumbers + 1);
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    return (
      <div className="pagination">
        <button className="info">Showing {startItem} to {endItem} of {integratedTotalElements} items</button>
        <div className="pagination-controls">
          <button onClick={() => setIntegratedCurrentPage(1)} className="pagination-btn" disabled={integratedCurrentPage === 1}><span className="small-pipe">|</span>&#8249;</button>
          <button onClick={() => setIntegratedCurrentPage(Math.max(1, integratedCurrentPage - 1))} className="pagination-btn" disabled={integratedCurrentPage === 1}>&#8249;</button>
          {pageNumbers.map((page) => (
            <button key={`integrated-page-${page}`} onClick={() => setIntegratedCurrentPage(page)} className={`pagination-btn ${integratedCurrentPage === page ? "active" : ""}`}>{page}</button>
          ))}
          <button onClick={() => setIntegratedCurrentPage(Math.min(integratedTotalPages, integratedCurrentPage + 1))} className="pagination-btn" disabled={integratedCurrentPage === integratedTotalPages}>&#8250;</button>
          <button onClick={() => setIntegratedCurrentPage(integratedTotalPages)} className="pagination-btn" disabled={integratedCurrentPage === integratedTotalPages}>&#8250;<span className="small-pipe">|</span></button>
        </div>
        <div>
          <select value={integratedItemsPerPage} onChange={(e) => { setIntegratedItemsPerPage(Number(e.target.value)); setIntegratedCurrentPage(1); }} className="items-perpage-select">
            <option value={5}>5</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
    );
  };
  const integratedControlTypes = getAllControlTypes(integratedAuditScores);
  const findingsDataChart = [
    { name: 'Not Implemented', value: data?.findings?.notImplemented || 0, color: '#9E9E9E' },
    { name: 'Initial / Ad-hoc', value: data?.findings?.initialAdhoc || 0, color: '#FFB74D' },
    { name: 'Partially Implemented', value: data?.findings?.partiallyImplemented || 0, color: '#FFD54F' },
    { name: 'Implemented but Needs Improvement', value: data?.findings?.implementedNeedsImprovement || 0, color: '#2196F3' },
    { name: 'Largely Effective', value: data?.findings?.largelyEffective || 0, color: '#4DB6AC' },
    { name: 'Best Practice / Optimized', value: data?.findings?.bestPractices || 0, color: '#4CAF50' },
  ];
  const hasFindingsData = findingsDataChart.some(item => item.value > 0);
  const findingsStatusData = [
    { name: 'Closed', value: data?.findings?.ncCurrentStatus?.closed || 0, color: '#4CAF50' },
    { name: 'Open', value: data?.findings?.ncCurrentStatus?.open || 0, color: '#FFC107' },
    { name: 'Deferred', value: data?.findings?.ncCurrentStatus?.deferred || 0, color: '#9C27B0' },
    { name: 'Work in Progress', value: data?.findings?.ncCurrentStatus?.work_in_progress || 0, color: '#b13e08ff' },
  ].filter(item => item.value > 0);
  const controlsData = data?.controlCounts ? [
    { name: 'AIMS', value: data.controlCounts.aims || 0, color: '#8E24AA' },
    { name: 'QMS', value: data.controlCounts.qms || 0, color: '#D81B60' },
    { name: 'BCMS', value: data.controlCounts.bcms || 0, color: '#00897B' },
    { name: 'CMMI', value: data.controlCounts.cmmi || 0, color: '#5C6BC0' },
    { name: 'PIMS', value: data.controlCounts.pims || 0, color: '#F57C00' },
    { name: 'ISMS', value: data.controlCounts.isms || 0, color: '#43A047' },
    { name: 'SOC1', value: data.controlCounts.soc1 || 0, color: '#E53935' },
    { name: 'SOC2', value: data.controlCounts.soc2 || 0, color: '#039BE5' },
    { name: 'ERM', value: data.controlCounts.erm || 0, color: '#6D4C41' },
    { name: 'ITSM', value: data.controlCounts.itsm || 0, color: '#1dd5ee' },
    { name: 'ERS', value: data.controlCounts.ers || 0, color: '#9ef83f' },
    { name: 'OHS', value: data.controlCounts.ohs || 0, color: '#802c12' },
  ] : [];
  const hasControlsData = controlsData.some(item => item.value > 0);
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
    });
  };
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Project Manager Dashboard</h1>
        <div className="dashboard-last-login">Last login: {new Date().toLocaleString()}</div>
      </div>
      <div className="dashboard-grid">
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Audit Summary</h2>
          {loading.auditSummary ? (
            <LoadingSpinner height={318} />
          ) : error.auditSummary || !data ? (
            <NoDataMessage height={318} />
          ) : (
            <>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.completedAudits || 0}</span>
                  <span className="dashboard-metric-label">Completed Audits</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.assignedAudits || 0}</span>
                  <span className="dashboard-metric-label">Assigned Audits</span>
                </div>
              </div>
              <div className="dashboard-chart-container">
                <div className="flex-1">
                  {hasFindingsData ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={findingsDataChart} cx="50%" cy="50%" outerRadius={50} labelLine={false} dataKey="value">
                          {findingsDataChart.map((entry, index) => (
                            <Cell key={`pm-findings-cell-${index}-${entry.name}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <NoDataMessage height={200} />
                  )}
                </div>
                <div className="flex-1">
                  {findingsDataChart.map((entry, index) => (
                    <div key={`findings-legend-${index}-${entry.name}`} className="pm-findings-legend-item">
                      <div className="findings-legend-color" style={{ backgroundColor: entry.color }} />
                      <span className="findings-legend-text"> {entry.name}: {entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Findings Status</h2>
          {loading.findingsStatus ? (
            <LoadingSpinner height={200} />
          ) : error.findingsStatus || !data?.findings?.ncCurrentStatus ? (
            <NoDataMessage height={200} />
          ) : (
            <div className="dashboard-chart-container">
              {findingsStatusData.length > 0 ? (
                <ResponsiveContainer width="95%" height={200} className="chart-mt-50">
                  <BarChart data={findingsStatusData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value">
                      {findingsStatusData.map((entry, index) => (
                        <Cell key={`pm-findings-status-cell-${index}-${entry.name}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <NoDataMessage height={200} />
              )}
            </div>
          )}
        </Card>
      </div>
      <div className="dashboard-grid mt-20">
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Current Projects</h2>
          {loading.projectChanges ? (
            <LoadingSpinner height={200} />
          ) : error.projectChanges || !data?.currentProjectChanges ? (
            <NoDataMessage height={200} />
          ) : (
            <>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.currentProjectChanges?.overall?.excluded ?? '0'}</span>
                  <span className="dashboard-metric-label">Excluded Projects (Overall)</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.currentProjectChanges?.overall?.additional ?? '0'}</span>
                  <span className="dashboard-metric-label">Additional Projects (Overall)</span>
                </div>
              </div>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.currentProjectChanges?.currentCampaign?.excluded ?? '0'}</span>
                  <span className="dashboard-metric-label">Excluded Projects (Current Campaign)</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.currentProjectChanges?.currentCampaign?.additional ?? '0'}</span>
                  <span className="dashboard-metric-label">Additional Projects (Current Campaign)</span>
                </div>
              </div>
            </>
          )}
        </Card>
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Control Counts</h2>
          {loading.controlsCount ? (
            <LoadingSpinner height={250} />
          ) : error.controlsCount || !hasControlsData ? (
            <NoDataMessage height={250} />
          ) : (
            <div className="dashboard-chart-container">
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={controlsData} cx="50%" cy="50%" outerRadius={50} labelLine={false} dataKey="value">
                      {controlsData.map((entry, index) => (
                        <Cell key={`pm-controls-cell-${index}-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1">
                {controlsData.map((entry, index) => (
                  <div key={`controls-legend-${index}-${entry.name}`} className="controls-legend-item">
                    <div className="controls-legend-color" style={{ backgroundColor: entry.color }}></div>
                    <span className="controls-legend-text"> {entry.name}: {entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
      <Card className="dashboard-card dashboard-full-width-card mt-20">
        <h2 className="dashboard-card-title">Projects Assigned</h2>
        {loading.projectsAssigned ? (
          <LoadingSpinner height={400} />
        ) : error.projectsAssigned || !data?.currentProjects ? (
          <NoDataMessage height={400} />
        ) : (
          <div className="dashboard-projects-container">
            {data.currentProjects.length > 0 ? (
              data.currentProjects.map((project: any) => (
                <div key={`pm-project-${project.projectId}`} className="dashboard-project-item">
                  <div className="dashboard-project-name">Project ID: {project.projectId}</div>
                  <div className="dashboard-project-name">Project Name: {project.projectName}</div>
                  <div className={`dashboard-project-status dashboard-project-status-${(project.status || '').toLowerCase().replace('_', '-')}`}>Project Status: {project.status?.replace('_', ' ')}</div>
                  <div className="dashboard-project">Start Date: {project.startDate ? formatDate(project.startDate) : 'Not Applicable'}</div>
                  <div className="dashboard-project">End Date: {project.endDate ? formatDate(project.endDate) : 'Not Applicable'}</div>
                  <div className="dashboard-project">Report Submitted: {project.reportSubmitted === true ? "Yes" : project.reportSubmitted === false ? "No" : project.reportSubmitted === null ? "Not Applicable" : formatDate(project.reportSubmitted)}</div>
                </div>
              ))
            ) : (
              <NoDataMessage height={400} />
            )}
          </div>
        )}
      </Card>
      <nav className="admin-dashboard-section-title mt-20">Integrated Audit Performance Scores</nav>
      <Card className="dashboard-card dashboard-full-width-card">
        <div className="audit-scores-header">
          <h2 className="dashboard-card-title">Integrated Audit Scores</h2>
          <FormControl className="campaign-filter-control" size="small">
            <InputLabel id="integrated-campaign-filter-label">Select Campaign</InputLabel>
            <Select labelId="integrated-campaign-filter-label" id="integrated-campaign-filter" value={selectedCampaignFilter} label="Select Campaign" onChange={handleCampaignFilterChange}>
              <MenuItem value="all">All Campaigns</MenuItem>
              {allCampaignNames.map((campaignName) => (
                <MenuItem key={campaignName} value={campaignName}>{campaignName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {loading.integratedAuditScores ? (
          <LoadingSpinner height={400} />
        ) : error.integratedAuditScores || integratedAuditScores.length === 0 ? (
          <div className="no-data-available">No integrated audit scores data available{selectedCampaignFilter !== "all" ? ` for campaign: ${selectedCampaignFilter}` : ''}</div>
        ) : (
          <>
            <TableContainer component={Paper} className="campaigns-table-container">
              <Table>
                <TableHead className="campaigns-table-header">
                  <TableRow>
                    <TableCell>Campaign Name</TableCell>
                    <TableCell>Project Name</TableCell>
                    <TableCell align="right">Overall Score</TableCell>
                    <TableCell align="center">Overall Rating</TableCell>
                    {integratedControlTypes.map((controlType, index) => (
                      <React.Fragment key={`control-header-${controlType}-${index}`}>
                        <TableCell align="right">{controlType} Weighted</TableCell>
                        <TableCell align="center">{controlType} Rating</TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell align="center">View Radar & Findings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className="campaigns-table-body">
                  {integratedAuditScores.map((auditScore: AuditScore, index: number) => {
                    const auditScoreData = auditScore.auditScoreData as any;
                    const overallScore = auditScoreData?.overallScore || 0;
                    const overallRating = auditScoreData?.overallRating || 'N/A';
                    const overallRatingClassName = getRatingClassName(overallRating);
                    return (
                      <TableRow key={`integrated-audit-${auditScore.auditId || index}`} hover className="campaign-body-row">
                        <TableCell>{auditScore.campaignName}</TableCell>
                        <TableCell>{auditScore.projectName}</TableCell>
                        <TableCell align="right"><span className="overall-score">{overallScore.toFixed(2)}%</span></TableCell>
                        <TableCell align="center"><span className={`rating-badge ${overallRatingClassName}`}>{overallRating}</span></TableCell>
                        {integratedControlTypes.map((controlType) => {
                          const control = getControlScoreByType(auditScore, controlType);
                          const ratingClassName = getRatingClassName(control?.rating || '');
                          return (
                            <React.Fragment key={`control-body-${controlType}-${auditScore.auditId || index}`}>
                              <TableCell align="right">
                                <span className="weighted-score">{control ? formatWeightedScore(control.weightedScore) : 'N/A'}</span>
                              </TableCell>
                              <TableCell align="center">
                                <span className={`rating-badge ${ratingClassName}`}>{control?.rating || 'N/A'}</span>
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                        <TableCell align="center">
                          <button className="radar-view-button" onClick={() => handleProjectClick(auditScore.projectId, auditScore.auditId, auditScore.projectName, auditScore.checklistType, auditScore.campaignId, auditScore.campaignName)}>View Radar & Findings</button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {renderIntegratedPagination()}
          </>
        )}
      </Card>
      <nav className="admin-dashboard-section-title mt-20">Normal Audit Performance Scores</nav>
      <Card className="dashboard-card dashboard-full-width-card">
        <div className="audit-scores-header">
          <h2 className="dashboard-card-title">Normal Audit Scores</h2>
          <FormControl className="campaign-filter-control" size="small">
            <InputLabel id="normal-campaign-filter-label">Select Campaign</InputLabel>
            <Select labelId="normal-campaign-filter-label" id="normal-campaign-filter" value={selectedCampaignFilter} label="Select Campaign" onChange={handleCampaignFilterChange}>
              <MenuItem value="all">All Campaigns</MenuItem>
              {allCampaignNames.map((campaignName) => (
                <MenuItem key={campaignName} value={campaignName}>{campaignName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {loading.normalAuditScores ? (
          <LoadingSpinner height={400} />
        ) : error.normalAuditScores || normalAuditScores.length === 0 ? (
          <div className="no-data-available">No normal audit scores data available{selectedCampaignFilter !== "all" ? ` for campaign: ${selectedCampaignFilter}` : ''}</div>
        ) : (
          <>
            <TableContainer component={Paper} className="campaigns-table-container">
              <Table>
                <TableHead className="campaigns-table-header">
                  <TableRow>
                    <TableCell>Campaign Name</TableCell>
                    <TableCell>Project Name</TableCell>
                    <TableCell align="right">Overall Score</TableCell>
                    <TableCell align="center">Overall Rating</TableCell>
                    <TableCell align="center">View Findings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className="campaigns-table-body">
                  {normalAuditScores.map((auditScore: AuditScore, index: number) => {
                    const auditScoreData = auditScore.auditScoreData as any;
                    const overallScore = auditScoreData?.overallScore || 0;
                    const overallRating = auditScoreData?.overallRating || 'N/A';
                    const overallRatingClassName = getRatingClassName(overallRating);
                    return (
                      <TableRow key={`normal-audit-${auditScore.auditId || index}`} hover className="campaign-body-row">
                        <TableCell>{auditScore.campaignName}</TableCell>
                        <TableCell>{auditScore.projectName}</TableCell>
                        <TableCell align="right"><span className="overall-score">{overallScore.toFixed(2)}%</span></TableCell>
                        <TableCell align="center"><span className={`rating-badge ${overallRatingClassName}`}>{overallRating}</span></TableCell>
                        <TableCell align="center">
                          <button className="radar-view-button" onClick={() => handleProjectClick(auditScore.projectId, auditScore.auditId, auditScore.projectName, auditScore.checklistType, auditScore.campaignId, auditScore.campaignName)}>View Findings</button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {renderNormalPagination()}
          </>
        )}
      </Card>
      <Dialog open={radarDialogOpen} onClose={handleCloseRadarDialog} maxWidth="xl" fullWidth>
        <DialogTitle className="radar-dialog-title">
          <div className="radar-dialog-header">
            <span>Control Performance - Project: {selectedProject?.projectName}</span>
            <IconButton onClick={handleCloseRadarDialog} className="close-button"><CloseIcon /></IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="radar-dialog-content">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Control Performance Radar</h3>
              {selectedProjectRadarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={selectedProjectRadarData}>
                    <PolarGrid gridType="polygon" />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={30} domain={[0, 5]} tickCount={7} />
                    <Radar name="Score" dataKey="score" stroke="#8884d8" strokeWidth={2} fill="#8884d8" fillOpacity={0.3} dot={(props: any) => {
                      const { cx, cy, index } = props;
                      return <circle key={index} cx={cx} cy={cy} r={5} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={1} />;
                    }} />
                    <Radar name="Weighted Score" dataKey="weightedScore" stroke="#82ca9d" strokeWidth={2} fill="#82ca9d" fillOpacity={0.3} dot={(props: any) => {
                      const { cx, cy, index } = props;
                      return <circle key={index} cx={cx} cy={cy} r={5} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={1} />;
                    }} />
                    <Legend />
                    <Tooltip formatter={(value: number, name: string) => [value.toFixed(2), name]} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">No radar data available for this project</div>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Findings by Control Type</h3>
              {integratedFindingsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}><CircularProgress /></Box>
              ) : integratedFindingsData?.findingsByControls && Object.keys(integratedFindingsData.findingsByControls).length > 0 ? (
                <TableContainer component={Paper} style={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Control Type</TableCell>
                        <TableCell align="center">Best Practice</TableCell>
                        <TableCell align="center">Largely Effective</TableCell>
                        <TableCell align="center">Needs Improvement</TableCell>
                        <TableCell align="center">Partially Implemented</TableCell>
                        <TableCell align="center">Initial / Adhoc</TableCell>
                        <TableCell align="center">Not Implemented</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(integratedFindingsData.findingsByControls).map(([controlType, findings]: [string, any]) => (
                        <TableRow key={controlType}>
                          <TableCell component="th" scope="row"><strong>{controlType}</strong></TableCell>
                          {findingOrder.map((findingKey) => (
                            <TableCell key={`${controlType}-${findingKey}`} align="center" className={getFindingClassName(findingKey, findings[findingKey] || 0)}>{findings[findingKey] || 0}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <div className="no-data">No findings data available for this project</div>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog open={findingsDialogOpen} onClose={handleCloseFindingsDialog} maxWidth="lg" fullWidth>
        <DialogTitle className="radar-dialog-title">
          <div className="radar-dialog-header">
            <span>Findings - Project: {selectedProject?.projectName} (Campaign: {selectedProject?.campaignName})</span>
            <IconButton onClick={handleCloseFindingsDialog} className="close-button"><CloseIcon /></IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="radar-dialog-content">
          {findingsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}><CircularProgress /></Box>
          ) : findingsData?.findingsByPracticeArea && Object.keys(findingsData.findingsByPracticeArea).length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Practice Area</TableCell>
                    <TableCell align="center">Best Practice</TableCell>
                    <TableCell align="center">Largely Effective</TableCell>
                    <TableCell align="center">Needs Improvement</TableCell>
                    <TableCell align="center">Partially Implemented</TableCell>
                    <TableCell align="center">Initial / Adhoc</TableCell>
                    <TableCell align="center">Not Implemented</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(findingsData.findingsByPracticeArea).map(([practiceArea, findings]: [string, any]) => (
                    <TableRow key={practiceArea}>
                      <TableCell component="th" scope="row"><strong>{practiceArea}</strong></TableCell>
                      {findingOrder.map((findingKey) => (
                        <TableCell key={`${practiceArea}-${findingKey}`} align="center" className={getFindingClassName(findingKey, findings[findingKey] || 0)}>{findings[findingKey] || 0}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div className="no-data">No findings data available for this project</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default PMDashboard;